/**
 * PLATE TAGS IMPORT UTILITY
 * 
 * This script imports plate data (tags, barcodes, storage) from a CSV file
 * into existing plates in Firebase.
 * 
 * Matching: Plates are matched by name + brand (case-insensitive)
 * 
 * Usage: Add this as a page/component in your app, or run the core logic
 * after authentication.
 */

import { collection, getDocs, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';

interface CSVPlateRow {
  name: string;
  brand: string;
  barcode: string;
  storage: string;
  tags: string[];
}

interface ImportResult {
  matched: number;
  updated: number;
  notFound: string[];
  errors: string[];
}

/**
 * Parse the CSV text into structured plate data
 */
export function parseCSV(csvText: string): CSVPlateRow[] {
  const lines = csvText.trim().split('\n');
  const plates: CSVPlateRow[] = [];
  
  // Skip header row
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    
    // Parse CSV - handle commas within fields
    const fields = parseCSVLine(line);
    
    if (fields.length >= 5) {
      const [name, brand, barcode, storage, tagsStr] = fields;
      
      // Parse pipe-separated tags
      const tags = tagsStr
        ? tagsStr.split('|').map(t => t.trim()).filter(t => t.length > 0)
        : [];
      
      plates.push({
        name: name.trim(),
        brand: brand.trim(),
        barcode: barcode.trim(),
        storage: storage.trim(),
        tags
      });
    }
  }
  
  return plates;
}

/**
 * Parse a single CSV line, handling quoted fields
 */
function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current); // Don't forget the last field
  
  return fields;
}

/**
 * Import plate data into Firebase
 * Matches by name + brand (case-insensitive)
 */
export async function importPlateData(
  csvPlates: CSVPlateRow[],
  onProgress?: (current: number, total: number, message: string) => void
): Promise<ImportResult> {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Must be logged in to import plates');
  }
  
  const result: ImportResult = {
    matched: 0,
    updated: 0,
    notFound: [],
    errors: []
  };
  
  // 1. Load all existing plates from Firebase
  onProgress?.(0, csvPlates.length, 'Loading existing plates...');
  
  const itemsRef = collection(db, 'users', currentUser.uid, 'items');
  const snapshot = await getDocs(itemsRef);
  
  // Build a lookup map: "brand|name" (lowercase) -> doc id & current data
  const existingPlates = new Map<string, { id: string; data: any }>();
  
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.itemType === 'plate') {
      const key = `${(data.brand || '').toLowerCase()}|${(data.name || '').toLowerCase()}`;
      existingPlates.set(key, { id: docSnap.id, data });
    }
  });
  
  // 2. Process CSV plates in batches
  const BATCH_SIZE = 500; // Firestore limit
  let batch = writeBatch(db);
  let batchCount = 0;
  
  for (let i = 0; i < csvPlates.length; i++) {
    const csvPlate = csvPlates[i];
    const key = `${csvPlate.brand.toLowerCase()}|${csvPlate.name.toLowerCase()}`;
    
    onProgress?.(i + 1, csvPlates.length, `Processing: ${csvPlate.brand} - ${csvPlate.name}`);
    
    const existing = existingPlates.get(key);
    
    if (!existing) {
      result.notFound.push(`${csvPlate.brand} - ${csvPlate.name}`);
      continue;
    }
    
    result.matched++;
    
    // Build update object - only include non-empty fields
    const updates: any = {};
    
    // Tags - merge with existing or replace
    if (csvPlate.tags.length > 0) {
      updates.tags = csvPlate.tags;
    }
    
    // Barcode - only if CSV has value and existing doesn't
    if (csvPlate.barcode && !existing.data.barcode) {
      updates.barcode = csvPlate.barcode;
    }
    
    // Storage - only if CSV has value and existing doesn't
    if (csvPlate.storage && !existing.data.storage) {
      updates.storage = csvPlate.storage;
    }
    
    // Only update if there's something to update
    if (Object.keys(updates).length > 0) {
      const itemRef = doc(db, 'users', currentUser.uid, 'items', existing.id);
      batch.update(itemRef, updates);
      batchCount++;
      result.updated++;
      
      // Commit batch if at limit
      if (batchCount >= BATCH_SIZE) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }
    }
  }
  
  // Commit any remaining updates
  if (batchCount > 0) {
    await batch.commit();
  }
  
  return result;
}

/**
 * Preview what would be imported (dry run)
 */
export async function previewImport(csvPlates: CSVPlateRow[]): Promise<{
  wouldMatch: number;
  wouldUpdate: number;
  notFound: string[];
  sampleUpdates: Array<{ name: string; brand: string; updates: string[] }>;
}> {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Must be logged in to preview import');
  }
  
  // Load existing plates
  const itemsRef = collection(db, 'users', currentUser.uid, 'items');
  const snapshot = await getDocs(itemsRef);
  
  const existingPlates = new Map<string, { id: string; data: any }>();
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.itemType === 'plate') {
      const key = `${(data.brand || '').toLowerCase()}|${(data.name || '').toLowerCase()}`;
      existingPlates.set(key, { id: docSnap.id, data });
    }
  });
  
  let wouldMatch = 0;
  let wouldUpdate = 0;
  const notFound: string[] = [];
  const sampleUpdates: Array<{ name: string; brand: string; updates: string[] }> = [];
  
  for (const csvPlate of csvPlates) {
    const key = `${csvPlate.brand.toLowerCase()}|${csvPlate.name.toLowerCase()}`;
    const existing = existingPlates.get(key);
    
    if (!existing) {
      notFound.push(`${csvPlate.brand} - ${csvPlate.name}`);
      continue;
    }
    
    wouldMatch++;
    
    const updates: string[] = [];
    
    if (csvPlate.tags.length > 0) {
      updates.push(`Tags: ${csvPlate.tags.length} tags`);
    }
    if (csvPlate.barcode && !existing.data.barcode) {
      updates.push(`Barcode: ${csvPlate.barcode}`);
    }
    if (csvPlate.storage && !existing.data.storage) {
      updates.push(`Storage: ${csvPlate.storage}`);
    }
    
    if (updates.length > 0) {
      wouldUpdate++;
      if (sampleUpdates.length < 10) {
        sampleUpdates.push({
          name: csvPlate.name,
          brand: csvPlate.brand,
          updates
        });
      }
    }
  }
  
  return { wouldMatch, wouldUpdate, notFound, sampleUpdates };
}
